import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import { Tabs, Tab } from "@mui/material";
import { ApiContext } from "../../context/MyContext";
import { useUtils } from "../../context/UtilsContext";
import { Toaster } from "../../common/Toaster/Toaster";
import Loader from "../../common/Loader/Loader";
import { fetchPlanCountryImages, fetchAllPlans } from "../../service/planCountryImageService";
import MainTabContent from "./MainTabContent";
import PlanTabContent from "./PlanTabContent";

const PlanCountryImages = () => {
  const { get, post } = useContext(ApiContext);
  useUtils();
  const [selectedTab, setSelectedTab] = useState("all");
  const [loading, setLoading] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("success");
  const [images, setImages] = useState([]);
  const [plans, setPlans] = useState([]);
  const [tabs, setTabs] = useState([{ key: "all", label: "All Images" }]);
  
  // Refs to prevent multiple calls
  const imagesLoadedRef = useRef(false);
  const plansLoadedRef = useRef(false);

  // Use dummy UUID for event type ID (temporary)
  const eventTypeId = "e77582c2-02d4-4a5d-b76d-07525808e4b0";

  // Show toast notification
  const showToast = useCallback((message, type = "success") => {
    setToastMessage(message);
    setToastType(type);
    setTimeout(() => setToastMessage(""), 3000);
  }, []);

  // Generate tabs from images data
  useEffect(() => {
    if (images.length === 0) {
      setTabs([{ key: "all", label: "All Images" }]);
      return;
    }

    // Extract unique plan names from plan_details
    const planNamesSet = new Set();
    let hasNoPlan = false;
    
    images.forEach((item) => {
      if (item.plan_details && Array.isArray(item.plan_details)) {
        item.plan_details.forEach((planDetail) => {
          if (planDetail.plan_name && planDetail.plan_name !== "no plan") {
            planNamesSet.add(planDetail.plan_name);
          } else if (planDetail.plan_name === "no plan" || planDetail.plan_id === null) {
            hasNoPlan = true;
          }
        });
      }
    });

    // Create tabs: Main tab first, then "No Plan" tab if exists, then plan tabs sorted alphabetically
    const planTabs = Array.from(planNamesSet)
      .sort()
      .map((planName) => ({
        key: planName,
        label: planName,
      }));

    const tabs = [{ key: "all", label: "All Images" }];
    
    // Add "No Plan" tab if there are images with no plan
    if (hasNoPlan) {
      tabs.push({ key: "no-plan", label: "No Plan" });
    }
    
    // Add other plan tabs
    tabs.push(...planTabs);

    setTabs(tabs);
  }, [images]);

  // Load images on mount (only once)
  useEffect(() => {
    if (imagesLoadedRef.current) return;
    imagesLoadedRef.current = true;

    const loadImages = async () => {
      setLoading(true);
      try {
        const imagesResult = await fetchPlanCountryImages(get);
        if (imagesResult.success) {
          setImages(imagesResult.data || []);
        } else {
          setToastMessage(imagesResult.message);
          setToastType("error");
          setTimeout(() => setToastMessage(""), 3000);
        }
      } catch (error) {
        console.error("Error loading images:", error);
        setToastMessage("Failed to load images. Please try again.");
        setToastType("error");
        setTimeout(() => setToastMessage(""), 3000);
      } finally {
        setLoading(false);
      }
    };

    loadImages();
  }, [get]);

  // Load plans on mount (only once)
  useEffect(() => {
    if (plansLoadedRef.current) return;
    plansLoadedRef.current = true;

    const loadPlans = async () => {
      try {
        const plansResult = await fetchAllPlans(get, eventTypeId);
        if (plansResult.success) {
          setPlans(plansResult.data || []);
        } else {
          setToastMessage(plansResult.message);
          setToastType("error");
          setTimeout(() => setToastMessage(""), 3000);
        }
      } catch (error) {
        console.error("Error loading plans:", error);
        setToastMessage("Failed to load plans. Please try again.");
        setToastType("error");
        setTimeout(() => setToastMessage(""), 3000);
      }
    };

    loadPlans();
  }, [get, eventTypeId]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  // Refresh data after operations
  const handleDataRefresh = useCallback(async () => {
    imagesLoadedRef.current = false;
    plansLoadedRef.current = false;
    
    setLoading(true);
    try {
      const [imagesResult, plansResult] = await Promise.all([
        fetchPlanCountryImages(get),
        fetchAllPlans(get, eventTypeId),
      ]);

      if (imagesResult.success) {
        setImages(imagesResult.data || []);
      } else {
        setToastMessage(imagesResult.message);
        setToastType("error");
        setTimeout(() => setToastMessage(""), 3000);
      }

      if (plansResult.success) {
        setPlans(plansResult.data || []);
      } else {
        setToastMessage(plansResult.message);
        setToastType("error");
        setTimeout(() => setToastMessage(""), 3000);
      }
    } catch (error) {
      console.error("Error refreshing data:", error);
      setToastMessage("Failed to refresh data. Please try again.");
      setToastType("error");
      setTimeout(() => setToastMessage(""), 3000);
    } finally {
      setLoading(false);
    }
  }, [get, eventTypeId]);

  return (
    <div className="flex-1 flex flex-col items-center">
      {toastMessage && <Toaster message={toastMessage} type={toastType} />}

      <div className="container px-2 py-4 mx-auto overflow-auto">
        {loading ? (
          <Loader />
        ) : (
          <>
            {/* Tab Header */}
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              textColor="inherit"
              TabIndicatorProps={{ style: { backgroundColor: "#FF6414" } }}
              sx={{
                "& .MuiTab-root": {
                  color: "#888",
                  "&.Mui-selected": { color: "#FF6414" },
                },
              }}
            >
              {tabs.map((tab) => (
                <Tab key={tab.key} label={tab.label} value={tab.key} />
              ))}
            </Tabs>

            {/* Tab Content */}
            <div className="w-full bg-app-bg rounded-lg p-10 shadow-lg mt-4">
              {selectedTab === "all" && (
                <MainTabContent
                  images={images}
                  plans={plans}
                  eventTypeId={eventTypeId}
                  get={get}
                  post={post}
                  showToast={showToast}
                  onDataRefresh={handleDataRefresh}
                />
              )}
              {selectedTab !== "all" && (
                <PlanTabContent
                  images={images}
                  planName={selectedTab === "no-plan" ? "no plan" : selectedTab}
                  eventTypeId={eventTypeId}
                  get={get}
                  post={post}
                  showToast={showToast}
                />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PlanCountryImages;

